import * as configcat from "configcat-js";
import { useEffect, useState } from "react";
import { DEFAULT_FEATURE_FLAGS, type FeatureFlags, type User } from "./authProvider.types";

let clientInstance: configcat.IConfigCatClient | null = null;

function getClient(sdkKey: string): configcat.IConfigCatClient {
  if (!clientInstance) {
    clientInstance = configcat.getClient(sdkKey, configcat.PollingMode.AutoPoll);
  }
  return clientInstance;
}

interface UseConfigCatFlagsOptions {
  user: User | null | undefined;
  sdkKey: string;
}

export function useConfigCatFlags({
  user,
  sdkKey,
}: UseConfigCatFlagsOptions): FeatureFlags | undefined {
  const [flags, setFlags] = useState<FeatureFlags | undefined>(undefined);
  const userSub = user?.sub;
  const userMail = user?.mail;
  const userTenantId = user?.tenantId;

  useEffect(() => {
    if (!userSub) {
      setFlags(undefined);
      return;
    }

    // No SDK key → sandbox / dev mode with no ConfigCat account. Return safe defaults
    // immediately so the app is fully functional without an external service.
    if (!sdkKey) {
      setFlags(DEFAULT_FEATURE_FLAGS);
      return;
    }

    const client = getClient(sdkKey);
    const configCatUser = new configcat.User(userSub, userMail, undefined, {
      ...(userTenantId ? { tenantId: userTenantId } : {}),
    });

    let cancelled = false;

    async function fetchFlags() {
      try {
        const [usePresence, useRealTimeAnnotations] = await Promise.all([
          client.getValueAsync("usePresence", false, configCatUser),
          client.getValueAsync("useRealTimeAnnotations", false, configCatUser),
        ]);

        if (!cancelled) {
          setFlags({ usePresence, useRealTimeAnnotations });
        }
      } catch (error) {
        console.error("Failed to fetch feature flags from ConfigCat:", error);
        if (!cancelled) {
          setFlags(DEFAULT_FEATURE_FLAGS);
        }
      }
    }

    fetchFlags();

    return () => {
      cancelled = true;
    };
  }, [userSub, userMail, userTenantId, sdkKey]);

  return flags;
}
